import { SHContainer, SHStack } from "@components/design-systems";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { StepCompProps, StepCompRef } from "@layouts/stepper";
import { AnalysisFilterDTO } from "@models/platform-analysis/entities/analysis";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { FeeInvestmentMenuOption } from "@models/platform-analysis/enums/fee/investmentMenu";
import { FeeMissingInvestmentsOption } from "@models/platform-analysis/enums/fee/missingInvestments";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { ProductsShortlistValidationStatus } from "@models/platform-analysis/enums/status";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { useTheme } from "@mui/material";
import { HasUndecidedPlatformDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/has-undecided-product";
import { NoProductShortlistedDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/no-shortlisted-product";
import { ShortlistedProductsExceededLimitDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/shortlisted-exceeded-limit";
import { FeeAnalysisSkeleton } from "@pages/platform-analysis-v2/_id/components/skeletons/fee-analysis";
import {
  checkStepIsLastVisibleStep,
  validateReviewSelectedProductsStatuses,
} from "@pages/platform-analysis-v2/_id/utils";
import { RootState } from "@redux/store";
import { savePlatformAnalysisFilters } from "@services/platform-analysis/platformAnalysisService";
import { isEqual } from "lodash";
import {
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { useToggle } from "react-use";
import { FeeAnalysisHeader } from "./components/fee-analysis-header";
import { FeeChartSection } from "./components/fee-chart-section";
import { FeeAnalysisProductComments } from "./components/fee-comment-section";
import { FeeSMAPortfolioSection } from "./components/fee-sma-portfolio-section";
import { generateHiddenSubProducts } from "./utils";

export type FeeAnalysisProps = StepCompProps;

export const FeeAnalysis: ForwardRefRenderFunction<
  StepCompRef,
  FeeAnalysisProps
> = ({ stepNumber }: FeeAnalysisProps, ref) => {
  //#region Hooks
  const { palette } = useTheme();
  const { analysisId } = useParams<{ analysisId: string }>();
  const {
    isReadOnly,
    isCompleted,
    loadPlatformAnalysis,
    resetPlatformAnalysisFee,
    disqualifyAllPlatforms,
    updateFinalJSONs,
    setHiddenIds,
  } = usePlatformAnalysis();

  const {
    ui: { isLoading, portfolioLevel, steps: storedSteps },
    platformAnalysis,
    internal: {
      feeAnalysis: {
        filterInvestmentMenuValue,
        filterMissingInvestmentValue,
        displayMode,
        displayStyle,
        heatmapMode,
        hiddenIds,
        performanceScope,
      },
    },
  } = useSelector((state: RootState) => state.platformAnalysis);
  //#endregion Hooks

  //#region Imperative handle
  useImperativeHandle(ref, () => ({
    onChangeStep: async (_, isNext) => {
      if (isReadOnly || isCompleted) return true;
      if (isNext) {
        const valid = await validateShortlistedPlatforms();
        if (valid) await saveFilter();

        return valid;
      }

      resetPlatformAnalysisFee();

      return true;
    },
    onChangeOtherStep: async () => {
      if (!isReadOnly) {
        await saveFilter();
      }
      resetPlatformAnalysisFee();

      return true;
    },
    onSubmit: async () => {
      if (!isReadOnly) {
        await saveFilter();
      }

      return true;
    },
    onExit: async () => {
      if (!isReadOnly) {
        await saveFilter();
      }

      return true;
    },
  }));
  //#endregion Imperative handle

  //#region States
  const [localIsLoading, toggleLocalLoading] = useToggle(true);
  const [showNoShortlistedDialog, toggleShowNoShortlistedDialog] =
    useToggle(false);
  const [isSubmittingDisqualifyDialog, toggleSubmittingDisqualifyDialog] =
    useToggle(false);
  const [
    showShortlistedExceededLimitDialog,
    toggleShowShortlistedExceededLimitDialog,
  ] = useToggle(false);
  const [disqualifyAllDialog, setDisqualifyAllDialog] = useState<
    | {
        callback: (value: boolean | Promise<boolean>) => void;
        numOfShortlistedProducts: number;
        numOfUndecidedProducts: number;
        numOfDisqualifiedProducts: number;
      }
    | undefined
  >(undefined);
  // #endregion States

  //#region Methods
  const loadData = async (analysisId?: string) => {
    if (!analysisId || analysisId === "new") return;
    await loadPlatformAnalysis({
      platformAnalysisId: analysisId,
      stepId: AnalysisStep.Fee,
    });
  };

  const saveFilter = async () => {
    const data: AnalysisFilterDTO = {
      feeMissingInvestmentsStyle:
        filterMissingInvestmentValue ?? FeeMissingInvestmentsOption.AllPlatform,
      investmentMenu:
        filterInvestmentMenuValue ?? FeeInvestmentMenuOption.AllPlatform,
      feesDisplayStyle: displayStyle,
      feeTableViewMode: heatmapMode,
      smaTableViewMode: displayMode,
      excludePerformanceInSMATable: isEqual(
        performanceScope,
        FeesPerformanceScope.Excluding,
      ),
      hiddenSubProducts: generateHiddenSubProducts(
        hiddenIds,
        platformAnalysis?.fee?.all.subProducts ?? [],
      ),
    };

    await savePlatformAnalysisFilters(platformAnalysis?.id ?? "", data);
  };

  const getDisqualifyAllPlatform = async (
    numOfShortlistedProducts: number,
    numOfUndecidedProducts: number,
    numOfDisqualifiedProducts: number,
  ): Promise<boolean> => {
    return await new Promise((res) => {
      setDisqualifyAllDialog({
        callback: res,
        numOfShortlistedProducts,
        numOfUndecidedProducts,
        numOfDisqualifiedProducts,
      });
    });
  };

  const handleOnDisqualifyAllPlatforms = async () => {
    if (!analysisId) return false;
    const isSuccess = await disqualifyAllPlatforms({
      analysisId,
      analysisStep: AnalysisStep.Fee,
    });

    return !!isSuccess;
  };

  const validateAndHandleReviewProducts = async (
    isLastVisibleStep: boolean,
  ): Promise<boolean> => {
    const selectedProducts = platformAnalysis?.fee?.all?.selectedProducts || [];
    const { status, numOfShortlisted, numOfDisqualified, numOfUndecided } =
      validateReviewSelectedProductsStatuses({
        selectedProducts,
        isLastVisibleStep,
      });

    switch (status) {
      case ProductsShortlistValidationStatus.ShortlistLimitReached:
        if (numOfUndecided === 0) return true;

        return await handleOnDisqualifyAllPlatforms();

      case ProductsShortlistValidationStatus.NoShortlisted:
        toggleShowNoShortlistedDialog(true);

        return false;

      case ProductsShortlistValidationStatus.HasUndecided:
        return await getDisqualifyAllPlatform(
          numOfShortlisted,
          numOfUndecided,
          numOfDisqualified,
        );

      case ProductsShortlistValidationStatus.ShortlistLimitExceeded:
        toggleShowShortlistedExceededLimitDialog(true);

        return false;

      default:
        return true;
    }
  };

  const validateShortlistedPlatforms = async (): Promise<boolean> => {
    if (!analysisId || analysisId === "new") return false;
    if (isCompleted || isReadOnly) return true;

    const isLastVisibleStep = checkStepIsLastVisibleStep({
      analysisSteps: storedSteps,
      stepToCheck: AnalysisStep.Fee,
    });

    // Proceed if not in review mode
    if (!platformAnalysis?.isReview) {
      if (isLastVisibleStep) await updateFinalJSONs(analysisId);

      return true;
    }

    // Validate review
    const isValid = await validateAndHandleReviewProducts(isLastVisibleStep);
    if (isValid && isLastVisibleStep) await updateFinalJSONs(analysisId);

    return isValid;
  };
  //#endregion Methods

  //#region Effects
  useEffect(() => {
    loadData(analysisId).then(() => toggleLocalLoading(false));
    // eslint-disable-next-line
  }, [analysisId]);
  //#endregion Effects

  if (isLoading || localIsLoading) return <FeeAnalysisSkeleton />;

  return (
    <>
      <SHContainer
        maxWidth="lg2"
        sx={{
          px: { xs: "16px", lg: 0 },
          py: "25px",
          "& .MuiChip-root": {
            borderColor: `${palette.warning.main} !important`,
          },
        }}
      >
        <SHStack spacing={"25px"}>
          <FeeAnalysisHeader stepNumber={stepNumber ?? 0} />
          <SHStack
            direction={{ xs: "column", lg: "row" }}
            justifyContent={{ xs: "stretch", lg: "space-between" }}
            spacing={"25px"}
            alignItems={"flex-start"}
          >
            <FeeAnalysisProductComments
              isReadOnly={isReadOnly}
              isCompleted={isCompleted}
            />
            <SHStack
              display={"flex"}
              flexGrow={1}
              width={{ xs: "100%", lg: "1px" }}
              sx={{
                "& .FeeSubProductCard-productName": {
                  fontWeight: "bold !important",
                },
                "& .FeeSubProductCard-subProductName": {
                  fontWeight: "normal !important",
                },
              }}
              spacing={"25px"}
            >
              <FeeChartSection onChangeHiddenIds={setHiddenIds} />
              {isEqual(portfolioLevel, FeeCalculatorPortfolioLevel.Family) && (
                <FeeSMAPortfolioSection
                  border={`1px solid ${palette.secondary[100]}`}
                  padding="28px"
                  backgroundColor={palette.common.white}
                />
              )}
            </SHStack>
          </SHStack>
        </SHStack>
      </SHContainer>

      <NoProductShortlistedDialog
        open={showNoShortlistedDialog}
        onClose={() => toggleShowNoShortlistedDialog(false)}
      />

      <ShortlistedProductsExceededLimitDialog
        open={showShortlistedExceededLimitDialog}
        onClose={() => toggleShowShortlistedExceededLimitDialog(false)}
      />

      <HasUndecidedPlatformDialog
        disqualifyProps={disqualifyAllDialog}
        isSubmitting={isSubmittingDisqualifyDialog}
        onClose={() => {
          disqualifyAllDialog?.callback(false);
          setDisqualifyAllDialog(undefined);
        }}
        onSubmit={async () => {
          toggleSubmittingDisqualifyDialog(true);
          handleOnDisqualifyAllPlatforms().then((res) => {
            disqualifyAllDialog?.callback(res);
            toggleSubmittingDisqualifyDialog(false);

            if (!res) return;
            setDisqualifyAllDialog(undefined);
          });
        }}
      />
    </>
  );
};
